import { globalCoverImages } from '@airship-platform/airship-patterns';
import { type OrganizationPresentationContentResponse } from '@airship-platform/proposal-presentations-feature/schema';
import {
  DEFAULT_COVER_IMAGE_IDS,
  coverPhotoTransformAssetPath,
  coverPhotoTransformQuery,
} from '@airship-platform/proposal-presentations-feature/utils';

// Every asset an offline presentation may render, to be pulled through the
// service worker's routes while the image signatures are still valid. The
// seeded default cover photos span two sources on purpose: the three theme
// defaults are owned by the shared cover-photo utils (the server resolves
// with the same ids), while the four stock images belong to the builder's
// picker in airship-patterns.
export const orgContentAssetUrls = (
  content: OrganizationPresentationContentResponse,
  getAssetUrl: (paths: string[]) => string
): string[] => [
  ...content.coreValues.flatMap((c) => (c.image?.url ? [c.image.url] : [])),
  ...content.brandAssets.flatMap((a) =>
    a.thumbnail?.url ? [a.thumbnail.url] : []
  ),
  ...content.promos.flatMap((p) => (p.image?.url ? [p.image.url] : [])),
  ...content.currentOrganizationTerms.map((terms) =>
    getAssetUrl([
      'organization-terms',
      content.organizationId,
      terms.termsId,
      'terms.html',
    ])
  ),
  ...content.proposalHeaderImages.flatMap((h) =>
    h.image?.url ? [h.image.url] : []
  ),
  // The organization logo the presentation renders (CoreValuesSection asks
  // OrganizationLogo for w=800). Without this warm-up an offline render's
  // <img> error swaps in the Airship logo in front of the customer. The
  // service worker caches by pathname only, so the query (incl. a logo the org
  // has never versioned) never causes a cache miss.
  `${getAssetUrl(['transforms', 'organization-logos', content.organizationId, 'logo'])}?w=800&fm=webp${
    content.organization.organizationLogoUrlVersion
      ? `&v=${content.organization.organizationLogoUrlVersion}`
      : ''
  }`,
  // Warmed as the same 1600px webp transform URLs the server and
  // resolveCoverPhoto emit — the shape is owned by coverPhoto.utils.
  ...[...DEFAULT_COVER_IMAGE_IDS, ...globalCoverImages].map(
    (id) =>
      `${getAssetUrl(coverPhotoTransformAssetPath(id))}?${coverPhotoTransformQuery()}`
  ),
];
